import React, { useCallback, useEffect, useRef, useState } from 'react';
import { WORKSPACE_TITLE_CLASS } from '../components/common/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import {
  SiteScaffold,
  SiteScaffoldPublishPlan,
  SiteScaffoldSpecVersion,
} from '../types';
import { useWebSocket } from '../contexts/WebSocketContext';
import SiteScaffoldPreview from '../components/sites/SiteScaffoldPreview';
import SiteScaffoldChatPane from '../components/sites/SiteScaffoldChatPane';
import MatchSiteBar from '../components/sites/MatchSiteBar';
import SiteSpecPanel from '../components/sites/SiteSpecPanel';
import SpecChangeStrip from '../components/sites/SpecChangeStrip';
import SiteScaffoldVersions from '../components/sites/SiteScaffoldVersions';
import StageModelsWidget from '../components/common/StageModelsWidget';
import {
  PageKind,
  PreviewScheme,
  SpecChange,
  contrastWarning,
  describeChanges,
  isOwnFrame,
  matchSitePrompt,
  previewTabUrl,
  readPreviewMessage,
  refineMatchPrompt,
  specSummary,
} from '../components/sites/siteSpec';

/**
 * The site designer: chat on the right, live preview on the left, one button
 * that commits the whole site to the user's repository.
 *
 * This exists because a HUGO site used to require a repo the user had already
 * built, themed and hosted themselves — we only ever committed markdown into
 * one. That blocked the case this is for: a main site on WordPress or Webflow
 * that we cannot touch, and a docs subdomain that we can own completely.
 */
export default function SiteScaffoldStudio() {
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const siteId = Number(siteIdParam);
  const navigate = useNavigate();

  const [scaffold, setScaffold] = useState<SiteScaffold | null>(null);
  const [kind, setKind] = useState<PageKind>('home');
  const [scheme, setScheme] = useState<PreviewScheme>('auto');
  // The page being read inside the preview, when a link was followed to reach
  // it. Empty means "whichever page this kind shows by default".
  const [path, setPath] = useState('');
  const [pageTitle, setPageTitle] = useState('');
  const [html, setHtml] = useState('');
  const [usingRealPages, setUsingRealPages] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState<SiteScaffoldPublishPlan | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [publishState, setPublishState] = useState<string>('');
  // A message the studio wants the chat to send — the "match a site" bar drives
  // the SAME agent rather than a side endpoint, so the work stays in one
  // transcript the user can read and follow up on.
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [chatBusy, setChatBusy] = useState(false);
  const [resetting, setResetting] = useState(false);
  // The chat and the fields are the SAME JOB done two ways — talk the design
  // into shape, then reach in and touch up a value. They do not need to be on
  // screen at once, and giving each half the studio left the preview, which is
  // the thing being judged, squeezed into a third of it.
  const [rightTab, setRightTab] = useState<'chat' | 'design' | 'history'>('chat');
  // Fields the agent moved while the design tab was hidden. Without this the
  // whole point of the panel — watching values change as the agent works — is
  // lost the moment it is behind a tab.
  const [unseenFields, setUnseenFields] = useState(0);
  // What the agent's last round moved, named, under the transcript. The bridge
  // between the two tabs: every entry opens that field in the design one.
  const [roundChanges, setRoundChanges] = useState<SpecChange[]>([]);
  const [focusPath, setFocusPath] = useState<string | null>(null);
  // The ring on a followed chip is a pointer, not a state. Cleared once the
  // panel says it has been seen, or it sits there for the rest of the session
  // and the next chip you follow adds a second one beside it.
  const clearFocusPath = useCallback(() => setFocusPath(null), []);
  const lastSpec = useRef<any>(null);
  const [versions, setVersions] = useState<SiteScaffoldSpecVersion[]>([]);
  // The version the preview is currently SHOWING. Browsing is free — rendering
  // a spec is a pure compile with no writes — so selecting one previews it and
  // nothing more; making it current is a second, deliberate press.
  const [previewVersion, setPreviewVersion] = useState<SiteScaffoldSpecVersion | null>(
    null,
  );
  const [restoring, setRestoring] = useState(false);
  // A REAL hugo build, on a worker. The preview settles structure — it comes
  // out of the same shell the layouts do — but not what Goldmark does with a
  // body, and that is the one question Python cannot answer about itself.
  const [building, setBuilding] = useState(false);
  const [builtHtml, setBuiltHtml] = useState<string | null>(null);

  const loadScaffold = useCallback(async () => {
    try {
      const fresh = await api.getSiteScaffold(siteId);
      // Diffed against what we last held rather than against the defaults: the
      // question the strip answers is "what did that round change", not "what
      // is non-default", and those are very different lists after ten rounds.
      const moved = lastSpec.current
        ? describeChanges(lastSpec.current, fresh.spec || {})
        : [];
      lastSpec.current = fresh.spec || {};
      if (moved.length) setRoundChanges(moved);
      setScaffold(fresh);
    } catch (e: any) {
      setError(e?.message || 'Could not load this site’s design.');
    }
  }, [siteId]);

  // What the frame is currently showing, as "kind|scheme". The effect below
  // renders when the buttons move away from this; see the comment there.
  const rendered = useRef<string>('');

  const renderPreview = useCallback(
    async (nextKind: PageKind, nextPath: string = '') => {
      setRendering(true);
      try {
        const result = await api.getSiteScaffoldPreview(
          siteId,
          nextKind,
          scheme,
          nextPath,
        );
        setHtml(result.html);
        setBuiltHtml(null);
        setUsingRealPages(result.using_real_pages);
        // The SERVER decides the kind when a path is given — a section and a
        // page are different templates, and it is the side that knows which
        // this path is. Taking the requested kind on trust would show the
        // wrong one and read as the click having failed.
        if (result.kind) setKind(result.kind as PageKind);
        rendered.current = `${result.kind || nextKind}|${scheme}`;
        setPath(result.path || '');
        setPageTitle(result.title || '');
        setError('');
      } catch (e: any) {
        setError(e?.message || 'Could not render the preview.');
      } finally {
        setRendering(false);
      }
    },
    [siteId, scheme],
  );

  /** Repaint from an unsaved edit. The preview endpoint is a pure compile — no
   *  LLM, no network, no writes — so this is cheap enough to run per keystroke. */
  const renderDraft = useCallback(
    async (nextSpec: any) => {
      try {
        const result = await api.previewSiteScaffoldSpec(
          siteId,
          nextSpec,
          kind,
          scheme,
          path,
        );
        setHtml(result.html);
        // A build is a snapshot of one moment. The instant the design moves,
        // what is on screen would be a picture of the previous one — which is
        // worse than no picture, because it still looks authoritative.
        setBuiltHtml(null);
        setUsingRealPages(result.using_real_pages);
        setError('');
      } catch (e: any) {
        // A half-typed hex is a rejected spec, and saying so on every keystroke
        // is noise. The field itself already shows it is not a colour yet.
        setError('');
      }
    },
    [siteId, kind, scheme, path],
  );

  const commitSpec = useCallback(
    async (nextSpec: any) => {
      try {
        const saved = await api.updateSiteScaffoldSpec(siteId, nextSpec);
        // Your own edit is not news — you are looking at the control you just
        // moved. Recording it keeps the strip about the AGENT's work.
        lastSpec.current = saved.spec || {};
        setScaffold(saved);
        await renderPreview(kind);
      } catch (e: any) {
        setError(e?.message || 'That change was rejected.');
      }
    },
    [siteId, kind, renderPreview],
  );

  const changeBrand = useCallback(
    async (brandProfileId: number | null, apply: boolean) => {
      try {
        const saved = await api.setSiteScaffoldBrand(siteId, brandProfileId, apply);
        lastSpec.current = saved.spec || {};
        setScaffold(saved);
        if (apply) await renderPreview(kind);
      } catch (e: any) {
        setError(e?.message || 'Could not switch the brand.');
      }
    },
    [siteId, kind, renderPreview],
  );

  // A click inside the preview. The frame runs in an opaque origin — it cannot
  // reach this document — so a posted message is the only way a click in there
  // can reach us, and the only thing it is allowed to send.
  //
  // CHECKED AGAINST OUR OWN FRAMES FIRST. This listener hears every message
  // posted to the page, including from an embedder or a window we opened, and
  // without the check any of them could drive the preview.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isOwnFrame(event.source)) return;
      const click = readPreviewMessage(event.data);
      if (!click) return;
      // The frame has no popup grant, so off-site links are opened out here.
      if (click.kind === 'external') window.open(click.url, '_blank', 'noopener,noreferrer');
      else renderPreview(kind, click.path);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [renderPreview, kind]);

  const loadVersions = useCallback(async () => {
    try {
      setVersions(await api.listSiteScaffoldVersions(siteId));
    } catch {
      // A history we could not fetch is not worth an error banner over the
      // design itself — the tab says it is empty and everything else works.
    }
  }, [siteId]);

  const showVersion = useCallback(
    async (version: SiteScaffoldSpecVersion | null) => {
      setPreviewVersion(version);
      if (version) await renderDraft(version.spec);
      else await renderPreview(kind);
    },
    // `renderDraft` closes over the colour scheme and the path being read, so
    // pinning this to `[kind]` handed a version to a renderer built before the
    // last scheme change — pick dark, then select a version, and it came back
    // light.
    [kind, renderDraft, renderPreview],
  );

  const applyVersion = useCallback(
    async (version: number) => {
      setRestoring(true);
      try {
        const fresh = await api.restoreSiteScaffoldVersion(siteId, version);
        lastSpec.current = fresh.spec || {};
        setScaffold(fresh);
        setPreviewVersion(null);
        setRoundChanges([]);
        await Promise.all([renderPreview(kind), loadVersions()]);
      } catch (e: any) {
        setError(e?.message || 'Could not go back to that version.');
      } finally {
        setRestoring(false);
      }
    },
    [siteId, kind, renderPreview, loadVersions],
  );

  const openInTab = useCallback(() => {
    window.open(previewTabUrl(siteId, kind, scheme), '_blank');
  }, [siteId, kind, scheme]);

  const buildForReal = useCallback(async () => {
    setBuilding(true);
    setBuiltHtml(null);
    setError('');
    try {
      await api.buildSiteScaffold(siteId, path);
    } catch (e: any) {
      setBuilding(false);
      setError(e?.message || 'Could not start the build.');
    }
  }, [siteId, path]);

  /** Wipe the DESIGN as well as the conversation. */
  const resetDesign = useCallback(async () => {
    const ok = window.confirm(
      'Reset the design?\n\nEvery color, font, header and nav item goes back to ' +
        'your brand defaults, and the conversation starts fresh.\n\nThe current ' +
        'design is saved to Versions first, so you can restore it.',
    );
    if (!ok) return;
    setResetting(true);
    try {
      const fresh = await api.resetSiteScaffold(siteId);
      lastSpec.current = fresh.spec || {};
      setScaffold(fresh);
      setRoundChanges([]);
      await renderPreview(kind);
      setPendingMessage(null);
    } catch (e: any) {
      setError(e?.message || 'Could not reset the design.');
    } finally {
      setResetting(false);
    }
  }, [siteId, kind, renderPreview]);

  useEffect(() => {
    loadScaffold();
  }, [loadScaffold]);

  useEffect(() => {
    // Refetched whenever the design moves, since every change writes one.
    loadVersions();
  }, [loadVersions, scaffold?.spec]);

  // The first paint, and the KIND and SCHEME buttons. Both buttons mean "show
  // this template's default page", which is why this is not keyed on `path`:
  // including it would make following a link render twice, the second time
  // undoing the first.
  //
  // ONE EFFECT, AND GUARDED. As two — one on `[kind]`, one on `[scheme]` —
  // every entry into the studio fired two identical renders on mount. And
  // reacting to `kind` at all is not quite right on its own: `renderPreview`
  // SETS it, because the server resolves a clicked path to whichever template
  // owns it, so a click that landed on a different template bounced you
  // straight back to that template's sample page. Comparing against what was
  // actually rendered distinguishes "you pressed a button" from "the server
  // told us what this page is".
  useEffect(() => {
    const want = `${kind}|${scheme}`;
    if (rendered.current === want) return;
    renderPreview(kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, scheme]);

  /** A chat round finished. Refetch the design; repaint only if it moved. */
  const onRoundFinished = useCallback(
    (specChanged: boolean) => {
      loadScaffold();
      if (specChanged) renderPreview(kind);
    },
    [loadScaffold, renderPreview, kind],
  );

  const { subscribe } = useWebSocket();
  useEffect(
    () =>
      subscribe((event: any) => {
        if (event?.site_id !== siteId) return;
        if (event.event_type === 'site_scaffold_build') {
          if (event.status === 'built') {
            setBuilding(false);
            setBuiltHtml(event.html || '');
          } else if (event.status === 'started') {
            setBuilding(true);
          } else {
            // `failed` and `unavailable` both end the wait. A failed build is
            // the ANSWER — it is what the user pressed the button to find out —
            // so hugo's own log goes on screen rather than a paraphrase.
            setBuilding(false);
            setError(
              [event.message, event.log].filter(Boolean).join('\n\n') ||
                'The build did not finish.',
            );
          }
          return;
        }
        if (event.event_type !== 'site_scaffold_publish') return;
        if (event.status === 'published') {
          setPublishState(`Committed ${String(event.commit_sha || '').slice(0, 7)}.`);
          setPlanOpen(false);
          loadScaffold();
        } else if (event.status === 'failed') {
          setPublishState(event.message || 'Publish failed.');
        } else {
          setPublishState('Committing…');
        }
      }),
    [subscribe, siteId, loadScaffold],
  );

  const openPlan = async () => {
    setPlanOpen(true);
    setPlan(null);
    setPublishState('');
    try {
      setPlan(await api.getSiteScaffoldPublishPlan(siteId));
    } catch (e: any) {
      setPublishState(e?.message || 'Could not work out what would be published.');
    }
  };

  const publish = async (overwrite: boolean) => {
    setPublishState('Committing…');
    try {
      await api.publishSiteScaffold(siteId, overwrite);
    } catch (e: any) {
      setPublishState(e?.message || 'Publish failed.');
    }
  };

  const spec = scaffold?.spec || {};
  const warning = contrastWarning(spec);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <div className="min-w-0">
          <h1 className={`${WORKSPACE_TITLE_CLASS} leading-tight`}>
            Site design
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {specSummary(spec)}
          </p>
        </div>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {/* On the right, in the action row, exactly as Deliver itself does it
              — this screen is that page's child and had it on the left.
              FIRST in the group and not last: it is a navigation control, and
              sitting it next to "Publish to GitHub" invites a misclick with a
              commit on the other end of it. */}
          <button
            onClick={() => navigate(`/deliver?site=${siteId}`)}
            title={`Back to ${scaffold?.site_name || 'this site'}`}
            className="shrink-0 max-w-[16rem] truncate px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 whitespace-nowrap"
          >
            ← Back to {scaffold?.site_name || 'this site'}
          </button>
          {warning && (
            <span
              title="Readability check, not a rule - fix it or ignore it."
              className="text-[11px] px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300"
            >
              {warning}
            </span>
          )}
          {scaffold?.published_commit_sha && !scaffold.spec_is_ahead && (
            <span className="text-[11px] px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
              Published
            </span>
          )}
          {scaffold?.spec_is_ahead && scaffold.published_commit_sha && (
            <span className="text-[11px] px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300">
              Changed since last publish
            </span>
          )}
          {/* Top right, where every other agent screen keeps it. It belongs to
              the page rather than to the chat pane: the same model reads a
              site's header on Match-a-site, which is not a chat action, so
              parking it inside the transcript hid it from half its own work. */}
          <StageModelsWidget
            surface="site_scaffold"
            returnLabel="the site designer"
            className="shrink-0"
          />
          {/* Named for what it WIPES, not for how it feels. "Start over" left
              people asking how to clear the design — which is exactly what it
              does — while a button in the chat pane wore the same words for a
              much smaller job. */}
          <button
            onClick={resetDesign}
            disabled={!scaffold || resetting || chatBusy}
            title="Put every color, font, header and nav item back to your brand defaults and start a fresh conversation. The current design is saved to Versions first, so you can restore it."
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {resetting ? 'Resetting…' : 'Reset design'}
          </button>
          <button
            onClick={openPlan}
            disabled={!scaffold}
            className="px-3 py-1.5 text-sm rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:opacity-90 disabled:opacity-50"
          >
            Publish to GitHub
          </button>
        </div>
      </div>

      {/* `min-w-0` on BOTH cells is load-bearing: without it a long chat bubble
          or a wide preview pushes the other pane into horizontal overflow.
          The right column is a FIXED width so the preview takes everything a
          wider window offers — it is the thing being judged. */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_28rem] min-h-0">
        <div className="min-w-0 border-r border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
          {previewVersion && (
            // Unmissable, because the fields tab beside it still describes the
            // CURRENT design — without this the two halves of the studio would
            // quietly disagree about which design is on screen.
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 text-xs">
              <span className="min-w-0 truncate">
                Previewing{' '}
                <strong>
                  {previewVersion.note || `version ${previewVersion.version}`}
                </strong>{' '}
 - not the current design.
              </span>
              <button
                type="button"
                disabled={restoring}
                onClick={() => applyVersion(previewVersion.version)}
                className="ml-auto px-2 py-0.5 rounded bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50 whitespace-nowrap"
              >
                {restoring ? 'Applying…' : 'Use this version'}
              </button>
              <button
                type="button"
                onClick={() => showVersion(null)}
                className="underline whitespace-nowrap"
              >
                Back to current
              </button>
            </div>
          )}
          {/* `flex-1 min-h-0` and not the preview's own `h-full`: the banner
              above it takes height off this cell, and 100% of the cell would
              then overflow it by exactly the banner. */}
          <div className="flex-1 min-h-0">
            <SiteScaffoldPreview
              html={builtHtml ?? html}
              kind={kind}
              onKindChange={setKind}
              loading={rendering}
              error={error || null}
              usingRealPages={usingRealPages}
              onOpenInTab={openInTab}
              onBuildForReal={buildForReal}
              building={building}
              scheme={scheme}
              onSchemeChange={setScheme}
              linksTo={scaffold?.public_base_url || null}
              path={path}
              pageTitle={pageTitle}
              onClearPath={() => renderPreview(kind)}
            />
          </div>
        </div>

        <div className="min-w-0 overflow-hidden flex flex-col">
          <MatchSiteBar
            disabled={chatBusy}
            suggestedUrl={scaffold?.public_base_url || null}
            savedUrl={scaffold?.reference_url || null}
            onMatch={(url, refine) => {
              setRightTab('chat');
              setPendingMessage(
                refine ? refineMatchPrompt(url, kind) : matchSitePrompt(url),
              );
            }}
          />

          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {/* TWO KINDS OF BADGE, and they must not look alike. The one on
                Design is an ALERT — fields moved while you were elsewhere, go
                and look. The one on History is a plain COUNT of what is in
                there. Wearing the same colour, a number that means "act on
                this" and a number that means "there are twelve" teach each
                other to be ignored. */}
            {(
              [
                { value: 'chat', title: 'Chat' },
                {
                  value: 'design',
                  title: 'Design',
                  badge: unseenFields,
                  alert: true,
                  badgeTitle: `${unseenFields} field${
                    unseenFields === 1 ? '' : 's'
                  } changed while you were on another tab - open Design to see which`,
                },
                {
                  value: 'history',
                  title: 'History',
                  badge: versions.length,
                  badgeTitle: `${versions.length} saved version${
                    versions.length === 1 ? '' : 's'
                  } you can go back to`,
                },
              ] as const
            ).map((tab) => (
              <button
                key={tab.value}
                onClick={() => {
                  // Leaving History drops the version preview. Otherwise the
                  // preview would keep showing an old design while the fields
                  // beside it described the current one.
                  if (tab.value !== 'history' && previewVersion) showVersion(null);
                  setRightTab(tab.value);
                }}
                className={`flex-1 px-3 py-2 text-sm border-b-2 -mb-px ${
                  rightTab === tab.value
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400 font-medium'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {tab.title}
                {'badge' in tab && tab.badge > 0 && (
                  <span
                    title={tab.badgeTitle}
                    className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                      'alert' in tab
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* BOTH STAY MOUNTED, hidden with CSS. Unmounting the chat would drop
              its websocket subscription and its scroll position, and unmounting
              the panel would throw away an edit in progress and the record of
              what the agent had just changed. */}
          <div
            className={`flex-1 min-h-0 flex flex-col ${
              rightTab === 'chat' ? '' : 'hidden'
            }`}
          >
            <div className="flex-1 min-h-0">
            <SiteScaffoldChatPane
              siteId={siteId}
              spec={spec}
              isPublished={Boolean(scaffold?.published_commit_sha)}
              onRoundFinished={onRoundFinished}
              pendingMessage={pendingMessage}
              onPendingConsumed={() => setPendingMessage(null)}
              onBusyChange={setChatBusy}
            />
            </div>
            <SpecChangeStrip
              changes={roundChanges}
              onDismiss={() => setRoundChanges([])}
              onOpenField={(path) => {
                setFocusPath(path);
                setRightTab('design');
              }}
            />
          </div>
          <div className={`flex-1 min-h-0 ${rightTab === 'design' ? '' : 'hidden'}`}>
            {scaffold && (
              <SiteSpecPanel
                spec={spec}
                defaults={scaffold.default_spec}
                brandProfiles={scaffold.brand_profiles || []}
                brandProfileId={scaffold.brand_profile_id ?? null}
                disabled={chatBusy || resetting}
                active={rightTab === 'design'}
                focusPath={focusPath}
                onFocusConsumed={clearFocusPath}
                onDraft={renderDraft}
                onCommit={commitSpec}
                onBrandChange={changeBrand}
                onUnseenChange={setUnseenFields}
              />
            )}
          </div>
          <div className={`flex-1 min-h-0 ${rightTab === 'history' ? '' : 'hidden'}`}>
            <SiteScaffoldVersions
              versions={versions}
              currentSpec={spec}
              selected={previewVersion?.version ?? null}
              busy={restoring}
              onSelect={showVersion}
              onUse={applyVersion}
            />
          </div>
        </div>
      </div>

      {planOpen && (
        <PublishDialog
          plan={plan}
          state={publishState}
          repoName={scaffold?.repo_full_name || null}
          onPublish={publish}
          onClose={() => setPlanOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * What a publish would do, shown before it does it.
 *
 * The list of files and the collisions are the whole point — an overwrite
 * confirmation with nothing to look at is a shrug, not a decision.
 */
function PublishDialog({
  plan,
  state,
  repoName,
  onPublish,
  onClose,
}: {
  plan: SiteScaffoldPublishPlan | null;
  state: string;
  repoName: string | null;
  onPublish: (overwrite: boolean) => void;
  onClose: () => void;
}) {
  const collisions = plan?.collisions || [];
  const busy = state === 'Committing…';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-medium text-gray-900 dark:text-gray-100">
            Publish to {repoName || 'the linked repository'}
          </h2>
          {plan && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              One commit on <code>{plan.branch}</code>. Your pages under{' '}
              <code>{plan.content_dir}/</code> are never touched.
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {!plan ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Working out what would be written…
            </p>
          ) : plan.blocked_reason ? (
            <p className="text-sm text-red-600 dark:text-red-400">{plan.blocked_reason}</p>
          ) : (
            <>
              {collisions.length > 0 && (
                <div className="mb-3 p-3 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-sm">
                  {collisions.length} file{collisions.length === 1 ? '' : 's'} already
                  exist and would be replaced. Publishing needs the overwrite option.
                </div>
              )}
              <table className="w-full text-xs">
                <tbody>
                  {plan.files.map((file) => (
                    <tr
                      key={file.path}
                      className="border-b border-gray-100 dark:border-gray-700/50"
                    >
                      <td className="py-1 pr-2 font-mono text-gray-700 dark:text-gray-300">
                        {file.path}
                      </td>
                      <td className="py-1 pr-2 text-right text-gray-400 dark:text-gray-500 whitespace-nowrap">
                        {(file.bytes / 1024).toFixed(1)} KB
                      </td>
                      <td className="py-1 text-right whitespace-nowrap">
                        {file.exists ? (
                          <span className="text-amber-600 dark:text-amber-400">
                            replaces
                          </span>
                        ) : (
                          <span className="text-green-600 dark:text-green-400">new</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {state && (
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">{state}</p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
          >
            Close
          </button>
          <button
            onClick={() => onPublish(collisions.length > 0)}
            disabled={!plan || Boolean(plan.blocked_reason) || busy}
            className={`px-4 py-1.5 text-sm rounded-md text-white disabled:opacity-50 ${
              collisions.length > 0
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {busy
              ? 'Committing…'
              : collisions.length > 0
                ? `Replace ${collisions.length} and publish`
                : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}
